import { CollapsibleSection, Spinner } from '@consigli/facade';
import {
  type AgreementContradiction as AgreementContradictionType,
  type ContradictionLink,
  type MeetingAgreement,
} from '@consigli/types';
import { useTranslation } from 'react-i18next';

import {
  CopyButton,
  ErrorAlert,
  EmptyState,
  LinkedAgreementCard,
  SeverityBadge,
  StatusBadge,
  ConflictTypeBadge,
  getSeverityCardStyles,
  calculateHighestSeverity,
  ProvisionalBadge,
} from './components';
import { TranscriptSourceModal } from './components/transcript-source-modal';
import { useAgreementTranscriptSource } from './hooks/use-agreement-transcript-source';
import { formatContradictionsForCopy } from './utils';

import type { Meeting, ParsedTranscriptEntry } from './utils/export-types';

interface AgreementContradictionProps {
  loading: boolean;
  generating?: boolean;
  hasFetched?: boolean;
  error: string | null;
  agreementContradictions: AgreementContradictionType[];
  hasTranscripts: boolean;
  meeting: Meeting;
  agreements?: MeetingAgreement[];
  transcriptEntries?: ParsedTranscriptEntry[];
  onRetry: (meeting: Meeting) => void;
}

export const AgreementContradiction = ({
  loading,
  generating = false,
  hasFetched = true,
  error,
  agreementContradictions,
  hasTranscripts,
  meeting,
  agreements = [],
  transcriptEntries,
  onRetry,
}: AgreementContradictionProps) => {
  const { t } = useTranslation();
  const isLoadingOrGenerating = loading || generating;
  const transcriptSource = useAgreementTranscriptSource(meeting, agreements, transcriptEntries);

  const severityLabels = {
    high: t('meeting-secretary.contradictions.severity-high'),
    medium: t('meeting-secretary.contradictions.severity-medium'),
    low: t('meeting-secretary.contradictions.severity-low'),
  };

  return (
    <div className="space-y-4">
      {loading && (
        <div className="flex items-center justify-center py-4">
          <Spinner size="xsmall" />
          <span className="ml-2 text-gray-600 text-sm">
            {t('meeting-secretary.contradictions.loading')}
          </span>
        </div>
      )}

      {!loading && generating && (
        <div className="flex items-center justify-center py-4">
          <Spinner size="xsmall" />
          <span className="ml-2 text-gray-600 text-sm">
            {t('meeting-secretary.contradictions.generating')}
          </span>
        </div>
      )}

      {error && (
        <ErrorAlert
          title={t('meeting-secretary.contradictions.failed-agreement')}
          message={error}
          onRetry={() => onRetry(meeting)}
          retryLabel={t('meeting-secretary.contradictions.try-again')}
        />
      )}

      {!isLoadingOrGenerating &&
        !error &&
        agreementContradictions.length === 0 &&
        !hasTranscripts && (
          <EmptyState
            icon="📝"
            title={t('meeting-secretary.contradictions.no-transcripts-title')}
            description={t('meeting-secretary.contradictions.no-transcripts-description')}
          />
        )}

      {!isLoadingOrGenerating &&
        !error &&
        hasFetched &&
        agreementContradictions.length === 0 &&
        hasTranscripts && (
          <EmptyState
            icon="📋"
            title={t('meeting-secretary.contradictions.no-contradictions-title')}
            description={t(
              'meeting-secretary.contradictions.no-agreement-contradictions-description',
            )}
          />
        )}

      {!loading && !generating && !error && agreementContradictions.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-gray-900">
              {t('meeting-secretary.contradictions.contradictions-found', {
                count: agreementContradictions.length,
              })}
            </h3>
            <CopyButton
              textToCopy={formatContradictionsForCopy(agreementContradictions, 'Contradiction', t)}
            />
          </div>

          {agreementContradictions.map((contradiction) => {
            const links = contradiction.agreementLinks || [];
            const highestSeverity = calculateHighestSeverity(links);

            return (
              <div
                key={contradiction.id}
                className={`p-4 border rounded-lg ${getSeverityCardStyles(highestSeverity)}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {contradiction.conflictType && (
                      <ConflictTypeBadge conflictType={contradiction.conflictType} />
                    )}
                    <SeverityBadge severity={highestSeverity} severityLabels={severityLabels} />
                    {contradiction.status && <StatusBadge status={contradiction.status} />}
                    {contradiction.provisional && <ProvisionalBadge />}
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">{contradiction.title}</h4>
                    <p className="text-sm text-gray-700">
                      {contradiction.contradictionDescription}
                    </p>
                  </div>

                  {links.length > 0 && (
                    <CollapsibleSection
                      className="border-t border-gray-200 pt-3 mt-3"
                      label={t(
                        links.length === 1
                          ? 'meeting-secretary.contradictions.linked-agreement'
                          : 'meeting-secretary.contradictions.linked-agreements',
                        { count: links.length },
                      )}
                    >
                      {links.map((link: ContradictionLink) => (
                        <LinkedAgreementCard
                          key={link.agreementId}
                          {...link}
                          onShowSource={
                            transcriptSource.hasTranscript
                              ? () => transcriptSource.showSource(link)
                              : undefined
                          }
                        />
                      ))}
                    </CollapsibleSection>
                  )}

                  {contradiction.notes && (
                    <div className="bg-gray-100 p-3 rounded mt-2">
                      <p className="text-xs text-gray-500 font-medium mb-1">
                        {t('meeting-secretary.contradictions.notes')}:
                      </p>
                      <p className="text-sm text-gray-700">{contradiction.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TranscriptSourceModal
        isOpen={!!transcriptSource.sourceRef}
        onClose={transcriptSource.closeSource}
        meeting={transcriptSource.meeting}
        entries={transcriptSource.entries}
        segmentIds={transcriptSource.segmentIds}
        isLoading={transcriptSource.isLoading}
        hasError={transcriptSource.hasError}
        onRetry={transcriptSource.retry}
      />
    </div>
  );
};
